import { PossibleAnswerDto, QuestionDto } from 'api/survey';
import { QuestionAnswerDto } from 'api/borrower';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QuickSurveyQuestion } from './quickSurvey.types';

interface UseQuickSurveyProps {
  surveyId: string;
  questions: QuestionDto[];
  existingAnswers?: QuestionAnswerDto[];
  defaultAnswerIndex?: number;
  onChange?: (answers: QuestionAnswerDto[]) => void;
}

export const useQuickSurvey = ({
  surveyId,
  questions,
  existingAnswers,
  defaultAnswerIndex = 0,
  onChange
}: UseQuickSurveyProps) => {
  // Map QuestionDto to simpler QuickSurveyQuestion
  const mappedQuestions: QuickSurveyQuestion[] = useMemo(() => {
    return questions
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((q) => ({
        id: q.id,
        key: q.key,
        label: q.label,
        possibleAnswers: q.possibleAnswers.sort(
          (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
        ),
        iconKey: q.iconKey,
        isRequired: q.isRequired
      }));
  }, [questions]);

  // State: map of questionId -> selectedAnswerId
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};

    // First, apply defaults
    mappedQuestions.forEach((q) => {
      if (q.possibleAnswers.length > defaultAnswerIndex) {
        const defaultAnswer = q.possibleAnswers[defaultAnswerIndex];
        if (defaultAnswer?.id) {
          initial[q.id] = defaultAnswer.id;
        }
      }
    });

    // Then override with existing answers
    existingAnswers?.forEach((a) => {
      if (a.possibleAnswerId) {
        initial[a.questionId] = a.possibleAnswerId;
      }
    });

    return initial;
  });

  // Sync existingAnswers when they change
  useEffect(() => {
    if (existingAnswers && existingAnswers.length > 0) {
      setAnswers((prev) => {
        const updated = { ...prev };
        existingAnswers.forEach((a) => {
          if (a.possibleAnswerId) {
            updated[a.questionId] = a.possibleAnswerId;
          }
        });
        return updated;
      });
    }
  }, [existingAnswers]);

  // Get selected answer ID for a question
  const getSelectedAnswerId = useCallback(
    (questionId: string): string | undefined => {
      return answers[questionId];
    },
    [answers]
  );

  // Convert state to QuestionAnswerDto array
  const toQuestionAnswerDtos = useCallback((): QuestionAnswerDto[] => {
    return Object.entries(answers).map(([questionId, possibleAnswerId]) => ({
      questionId,
      possibleAnswerId,
      answerText: null,
      answerValue: null,
      multiplePossibleAnswerIds: []
    }));
  }, [answers]);

  // Handle answer selection (no dialog, direct update)
  const handleSelectAnswer = useCallback(
    (questionId: string, answer: PossibleAnswerDto) => {
      if (!answer.id) return;

      setAnswers((prev) => ({
        ...prev,
        [questionId]: answer.id!
      }));
    },
    []
  );

  // Notify parent of changes
  useEffect(() => {
    onChange?.(toQuestionAnswerDtos());
  }, [answers, onChange, toQuestionAnswerDtos]);

  return {
    mappedQuestions,
    answers,
    getSelectedAnswerId,
    handleSelectAnswer,
    toQuestionAnswerDtos
  };
};
